/**
 * BrawlArena — brawl-arena.
 *
 * Purpose-built PvP testing ground. Distinct from dj-live-lobby (which is
 * multi-purpose social hub). Brawl arena: open the URL, fight immediately,
 * fast respawn loop, no friction.
 *
 * Per orchestrator brief 2026-04-26:
 * - 4 respawn points spread (NW/NE/SW/SE) so kills don't clump same-spot
 * - Walled on all sides (no falling off — the arena IS the playable space)
 * - 2 cover pillars in the interior for combat sight-breaks
 * - Distinct visual palette from lobby (dark crimson — reads as "fight zone")
 * - PvP defaults baked in via metadata.pvp (faster respawn, no penalty,
 *   bots fight everyone)
 *
 * Tile layout (100×60 tiles = 3200×1920 px):
 *
 *     x=0 ────────────────────── x=99
 *   y=0  ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓  ← ceiling wall (y=0..3)
 *        ▓                          ▓
 *        ▓                          ▓
 *        ▓                          ▓
 *        ▓                          ▓
 *        ▓        ▓▓        ▓▓      ▓  ← cover pillars (35,45)..(36,50)
 *        ▓        ▓▓        ▓▓      ▓     and (63,45)..(64,50)
 *        ▓                          ▓
 *        ▓ · F-W   · W   · E   · F-E ▓  ← 4 spawn points spread along
 *  y=53  ▓══════════════════════════▓     the floor at x=15/38/62/85
 *        ▓░░░░░░░░░░░░░░░░░░░░░░░░░░▓
 *  y=59  ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
 *
 * Respawn behavior: type='field' + 4 spawnPoints + no defaultSpawn →
 * MapSystem.getDefaultSpawnPosition randomly picks one of the 4 each
 * respawn (added 2026-04-26 alongside this map). Kills don't pile up
 * at the same coords. Spawn points are co-planar on the floor because
 * elevated landings would get snapped down by validateOnGround anyway.
 *
 * Shipped 2026-04-26 as part of PvP arc Phase 3 hardening.
 */

import { PlatformerLevel } from '../../shared/CharacterTypes';
import { GridLevel, LayerType } from '../../shared/TileEditorTypes';
import { BRAWL_CONFIG } from '../../config/brawl/BrawlConfig';

const MAP_ID = 'brawl-arena';
const MAP_NAME = 'Brawl Arena';
const VERSION = '1.0';

const T = 32;          // tile size (px)
const W = 200;         // tiles wide → 6400 px (resized 2026-04-29 from 100)
const H = 60;          // tiles tall → 1920 px

const WALL_THICKNESS = 4;  // tiles
const FLOOR_TOP_ROW = 53;  // tiles — top of the floor (playable space ends here)
const FLOOR_DEPTH = 6;     // tiles down to the bottom wall

// 4 respawn points — NW/NE high corners + SW/SE floor corners. The high
// corners are AERIAL (no landing platform under them); MapSystem's new
// raycast-snap mode (`metadata.spawnSnapMaxFallPx` below) suppresses the
// validateOnGround snap when the only platform is too far to count as
// "near enough", so the player drops in via gravity instead of
// teleporting to the floor. The two floor corners use the original
// snap behavior (platform is right there).
//
// Spread evenly across the now-200-wide arena.
const SPAWN_TILES = [
  { name: 'NW', x: 20,  y: 10 },  // high-aerial NW
  { name: 'NE', x: 180, y: 10 },  // high-aerial NE
  { name: 'SW', x: 70,  y: 51 },  // floor SW
  { name: 'SE', x: 130, y: 51 },  // floor SE
];
const spawnPoints = SPAWN_TILES.map(s => ({ x: s.x * T, y: s.y * T }));

// Intentionally NO defaultSpawn — that triggers the random-pick-from-
// spawnPoints behavior in MapSystem.getDefaultSpawnPosition. With
// `type: 'field'`, every respawn re-rolls instead of returning to
// "last known position".

// ============================================================================
// RUNTIME PLATFORMS — walls + floor + cover pillars
// ============================================================================
const runtimePlatforms = [
  // Floor (full width)
  {
    id: 'floor',
    x: 0,
    y: FLOOR_TOP_ROW * T,
    width: W * T,
    height: FLOOR_DEPTH * T,
    type: 'normal' as const,
    color: '#3a1a1a',  // dark crimson floor
  },
  // Ceiling (full width — caps jumps so no escape)
  {
    id: 'ceiling',
    x: 0,
    y: 0,
    width: W * T,
    height: WALL_THICKNESS * T,
    type: 'normal' as const,
    color: '#2a0e0e',
  },
  // Left wall
  {
    id: 'wall-left',
    x: 0,
    y: 0,
    width: WALL_THICKNESS * T,
    height: H * T,
    type: 'normal' as const,
    color: '#2a0e0e',
  },
  // Right wall
  {
    id: 'wall-right',
    x: (W - WALL_THICKNESS) * T,
    y: 0,
    width: WALL_THICKNESS * T,
    height: H * T,
    type: 'normal' as const,
    color: '#2a0e0e',
  },
  // Cover pillars — type:'cover' = walk-through for players, blocks
  // projectiles + LOS (CollisionResolver skips cover, ProjectileSystem
  // treats it as solid, LineOfSight blocks on it). 3 pillars at 50-tile
  // intervals across the 200-tile arena.
  ...[50, 100, 150].map((tileX, i) => ({
    id: `pillar-${i + 1}`,
    x: (tileX - 1) * T,        // center the 2-tile pillar on tileX
    y: 45 * T,
    width: 2 * T,
    height: 8 * T,
    type: 'cover' as const,
    color: '#5a2a2a',
  })),
  // Ruler ticks — non-interactive decorative tiles labelled with the
  // tile-x position from the left wall (x=0). Every 25 tiles, sitting
  // on top of the floor. type:'decorative' means no collision, no
  // projectile block, no LOS block — purely visual measurement aid.
  ...[25, 50, 75, 100, 125, 150, 175].map((tileX) => ({
    id: `ruler-${tileX}`,
    x: tileX * T - 2,            // 4-px wide tick centered on tileX
    y: (FLOOR_TOP_ROW - 1) * T,  // sits 1 tile above floor top
    width: 4,
    height: T,
    type: 'decorative' as const,
    color: '#ffffff',
    label: String(tileX),
  })),
];

// ============================================================================
// PARALLAX — moody crimson backdrop, distinct from lobby
// ============================================================================

// ============================================================================
// EXPORT
// ============================================================================
export const BrawlArena: GridLevel & Partial<PlatformerLevel> = {
  id: MAP_ID,
  name: MAP_NAME,
  version: VERSION,

  widthTiles: W,
  heightTiles: H,
  width: W * T,
  height: H * T,
  tileSize: T,

  backgroundColor: '#0a0303',  // very dark crimson — reads "fight zone" at a glance
  gravity: 1000,

  spawnPoints,
  // No defaultSpawn — see MapSystem.getDefaultSpawnPosition (random-pick when
  // multiple spawnPoints + no defaultSpawn).


  layers: [
    { name: LayerType.GROUND,    visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.COLLISION, visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.OBJECTS,   visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.ENTITIES,  visible: true, locked: false, opacity: 1.0, tiles: new Map(), entities: [] },
    { name: LayerType.TRIGGERS,  visible: true, locked: false, opacity: 1.0, tiles: new Map() },
  ],

  platforms: runtimePlatforms,
  entities: [],
  spawnRules: [],
  type: 'field',  // 'field' triggers respawn-via-getDefaultSpawnPosition (random pick)

  metadata: {
    author: 'DJ',
    description: 'PvP brawl arena — 100×60, walled, 4 respawn points, 2 cover pillars, fast respawn loop. Ships with PvP defaults baked into metadata.',
    tags: ['pvp', 'arena', 'brawl', 'testing'],
    // Lobby tools available so DJ can spawn bots for solo brawl.
    lobbyTools: { enabled: true },
    // Raycast-snap mode: only snap-to-ground when the nearest platform
    // is within 320px (~10 tiles). Beyond that, leave the position
    // aerial — gravity drops the player visibly instead of teleporting
    // them. Lets the NW/NE high-corner spawns work as authored.
    spawnSnapMaxFallPx: 320,
    // Map rule profile — generic PvP arena defaults pulled from
    // PROFILES['pvp_arena'] (src/shared/config/map_profiles/pvp_arena.ts).
    // Roster-shaped knobs (count, classes, tiers, names, positions)
    // are brawl-specific and live in `overrides.bot` driven by
    // BRAWL_CONFIG (src/config/brawl/BrawlConfig.ts).
    //
    // PvP knobs (`overrides.pvp`) carry BRAWL_CONFIG's pvpEnabled /
    // friendlyFire / respawn timings so BRAWL_CONFIG stays "the single
    // visible knob" for /brawl. They happen to match pvp_arena's
    // profile defaults today, but explicitly re-declaring them keeps
    // BRAWL_CONFIG load-bearing — change a value there and brawl
    // behavior changes.
    //
    // L1 Phase 2 (2026-04-30) dropped the legacy metadata.pvp /
    // metadata.bot mirror blocks. Profile + overrides is now the sole
    // declaration. resolvePvPConfig / resolveBotConfig delegate to
    // resolveMapManifest internally so any remaining direct callers
    // still get the same resolved values.
    profile: 'pvp_arena',
    overrides: {
      pvp: {
        enabled: BRAWL_CONFIG.pvpEnabled,
        friendlyFire: BRAWL_CONFIG.friendlyFire,
        respawnDelayMs: BRAWL_CONFIG.respawnDelayMs,
        respawnInvulnerabilityMs: BRAWL_CONFIG.respawnInvulnerabilityMs,
        botBehavior: BRAWL_CONFIG.botBehavior,
      },
      bot: {
        lobbyCount: BRAWL_CONFIG.botRoster.length,
        lobbyClasses: BRAWL_CONFIG.botRoster.map((b) => b.class),
        spawnRingRadius: 0,
        colorScheme: 'distinct',
        behaviorTier: BRAWL_CONFIG.botRoster[0]?.tier ?? 'dummy',
        lobbyTiers: BRAWL_CONFIG.botRoster.map((b) => b.tier),
        lobbyNames: BRAWL_CONFIG.botRoster.map((b) => b.name),
        lobbyPositions: BRAWL_CONFIG.botRoster.map((b) => ({
          x: b.tileX * 32,
          y: (53 - 1) * 32,
        })),
      },
    },
  },
};

export { W, H, T, MAP_ID, MAP_NAME, SPAWN_TILES };
